import { useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2, Upload } from 'lucide-react'

import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import { Textarea } from 'components/ui/textarea'
import { cn } from 'lib/utils'

import type { ConfigField, ConfigSchema, EntityRef } from '../types/presentation.types'
import { sampleConfig } from '../utils/configDefaults'
import { PhotoField } from './PhotoField'

/**
 * A form generated from an element's `configSchema` (MYD-495).
 *
 * The server owns the schema (backend/presentations/configSchema.js), so an
 * element added there gets an inspector form here with no frontend change.
 * Each field type maps to one control:
 *
 *   string    Input          markdown  Textarea
 *   number    Input[number]  boolean   Switch
 *   enum      Select         url       Input, plus Upload for images
 *   list      repeatable rows, each an editor for `of`
 *   object    a nested group of fields
 *   photo     the source location's photos (MYD-499); a list of them is one
 *             multi-pick grid rather than rows, since a row cannot start blank
 *
 * The form reports the WHOLE config on every change; the editor coalesces
 * consecutive config edits to one block into one undo step. Nothing is
 * validated here beyond the obvious (lengths, number bounds): the server
 * re-validates on save and names the field at fault.
 */

interface ConfigFormProps {
	schema: ConfigSchema
	value: Record<string, unknown>
	onChange: (next: Record<string, unknown>) => void
	/** Prefix for element ids, so two forms on a page never collide. */
	idPrefix: string
	/** The asset the block draws on, for fields that pick from it (photos). */
	source?: EntityRef | null
}

/** "maxItems" becomes "Max items". Schemas carry no labels, so field names are it. */
export const humanize = (name: string) =>
	name.replace(/([a-z0-9])([A-Z])/g, (_, before: string, capital: string) => `${before} ${capital.toLowerCase()}`).replace(/^./, first => first.toUpperCase())

export function ConfigForm({ schema, value, onChange, idPrefix, source = null }: ConfigFormProps) {
	const entries = Object.entries(schema)
	if (entries.length === 0) return <p className="text-sm text-muted-foreground">This element has no settings.</p>

	return (
		<div className="space-y-4">
			{entries.map(([name, field]) => (
				<FieldEditor
					key={name}
					id={`${idPrefix}-${name}`}
					name={name}
					field={field}
					value={value[name]}
					source={source}
					onChange={next => onChange({ ...value, [name]: next })}
				/>
			))}
		</div>
	)
}

interface FieldEditorProps {
	id: string
	name: string
	field: ConfigField
	value: unknown
	onChange: (next: unknown) => void
	source: EntityRef | null
	/** Inside a list row the row supplies the context, so the label is dropped. */
	bare?: boolean
}

function FieldEditor({ id, name, field, value, onChange, source, bare }: FieldEditorProps) {
	const label = (
		<Label htmlFor={id} className="text-xs font-medium text-gray-600">
			{humanize(name)}
			{field.required && <span className="text-destructive"> *</span>}
		</Label>
	)

	const control = (() => {
		switch (field.type) {
			case 'string':
				return (
					<Input id={id} value={typeof value === 'string' ? value : ''} maxLength={field.max} onChange={event => onChange(event.target.value)} />
				)
			case 'markdown':
				return (
					<>
						<Textarea
							id={id}
							rows={6}
							value={typeof value === 'string' ? value : ''}
							maxLength={field.max}
							onChange={event => onChange(event.target.value)}
						/>
						<p className="text-[11px] text-muted-foreground">**bold**, *italic*, and lines starting with "- " for bullets.</p>
					</>
				)
			case 'number':
				return (
					<Input
						id={id}
						type="number"
						min={field.min}
						max={field.max}
						// Unset shows the default the server will apply, greyed as a hint.
						placeholder={typeof field.default === 'number' ? String(field.default) : undefined}
						value={typeof value === 'number' ? value : ''}
						// An emptied box clears the value rather than storing NaN.
						onChange={event => onChange(event.target.value === '' ? undefined : Number(event.target.value))}
					/>
				)
			case 'boolean':
				// An unset value shows the schema's default, which is what the server
				// will apply: a switch drawn "off" for a default-on field would lie.
				return <Switch id={id} checked={(value ?? field.default) === true} onChange={event => onChange(event.target.checked)} />
			case 'enum':
				return (
					<Select id={id} value={String(value ?? field.default ?? '')} onChange={event => {
						const picked = field.values.find(option => String(option) === event.target.value)
						onChange(picked)
					}}>
						{field.values.map(option => (
							<option key={String(option)} value={String(option)}>
								{field.labels?.[String(option)] ?? humanize(String(option))}
							</option>
						))}
					</Select>
				)
			case 'url':
				return <UrlField id={id} value={typeof value === 'string' ? value : ''} hosts={field.hosts} onChange={onChange} />
			case 'photo':
				return <PhotoField id={id} source={source} value={typeof value === 'string' ? value : undefined} onChange={onChange} />
			case 'list':
				if (field.of.type === 'photo') {
					return <PhotoField id={id} source={source} value={Array.isArray(value) ? (value as string[]) : []} max={field.max} onChange={onChange} />
				}
				return <ListField id={id} name={name} field={field} value={Array.isArray(value) ? value : []} source={source} onChange={onChange} />
			case 'object':
				return (
					<div className="space-y-3 rounded-md border border-gray-200 p-3">
						<ConfigForm
							schema={field.fields}
							idPrefix={id}
							value={value && typeof value === 'object' ? (value as Record<string, unknown>) : {}}
							source={source}
							onChange={onChange}
						/>
					</div>
				)
			default:
				return null
		}
	})()

	if (bare) return control
	return (
		<div className={cn('space-y-1.5', field.type === 'boolean' && 'flex items-center justify-between space-y-0')}>
			{label}
			{control}
		</div>
	)
}

/**
 * An image URL. Only the schema's hosts are accepted (Cloudinary), so the
 * normal path is Upload, which goes through the same unsigned Cloudinary
 * upload the rest of the app uses. Pasting a Cloudinary URL also works.
 */
function UrlField({ id, value, hosts, onChange }: { id: string; value: string; hosts: string[]; onChange: (next: unknown) => void }) {
	const fileRef = useRef<HTMLInputElement>(null)
	const [uploading, setUploading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const upload = async (file: File) => {
		setUploading(true)
		setError(null)
		try {
			const result = await uploadToCloudinary(file, { folder: 'presentations', resourceType: 'image' })
			onChange(result.secure_url)
		} catch {
			setError('The upload failed. Try again.')
		} finally {
			setUploading(false)
		}
	}

	return (
		<div className="space-y-1.5">
			<div className="flex gap-2">
				<Input id={id} value={value} placeholder={`https://${hosts[0]}/...`} onChange={event => onChange(event.target.value)} />
				<button
					type="button"
					onClick={() => fileRef.current?.click()}
					disabled={uploading}
					className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
				>
					<Upload className="h-3.5 w-3.5" />
					{uploading ? 'Uploading...' : 'Upload'}
				</button>
				<input
					ref={fileRef}
					type="file"
					accept="image/*"
					className="hidden"
					onChange={event => {
						const file = event.target.files?.[0]
						if (file) upload(file)
						event.target.value = ''
					}}
				/>
			</div>
			{error && <p className="text-xs text-destructive">{error}</p>}
		</div>
	)
}

/** Repeatable rows: add, remove, reorder. Capped at the schema's `max`. */
function ListField({
	id,
	name,
	field,
	value,
	source,
	onChange,
}: {
	id: string
	name: string
	field: Extract<ConfigField, { type: 'list' }>
	value: unknown[]
	source: EntityRef | null
	onChange: (next: unknown) => void
}) {
	const atCap = value.length >= field.max

	const blankItem = () => {
		if (field.of.type === 'string' || field.of.type === 'markdown') return ''
		if (field.of.type === 'object') return sampleConfig(field.of.fields) ?? {}
		return field.of.default ?? null
	}

	const replace = (index: number, next: unknown) => onChange(value.map((item, at) => (at === index ? next : item)))
	const remove = (index: number) => onChange(value.filter((_, at) => at !== index))
	const move = (index: number, delta: -1 | 1) => {
		const to = index + delta
		if (to < 0 || to >= value.length) return
		const next = [...value]
		;[next[index], next[to]] = [next[to], next[index]]
		onChange(next)
	}

	return (
		<div className="space-y-2">
			{value.map((item, index) => (
				<div key={index} className="flex items-start gap-1.5">
					<div className="min-w-0 flex-1">
						<FieldEditor id={`${id}-${index}`} name={`${name} ${index + 1}`} field={field.of} value={item} source={source} onChange={next => replace(index, next)} bare />
					</div>
					<div className="flex shrink-0 flex-col">
						<IconButton label="Move up" disabled={index === 0} onClick={() => move(index, -1)}>
							<ArrowUp className="h-3.5 w-3.5" />
						</IconButton>
						<IconButton label="Move down" disabled={index === value.length - 1} onClick={() => move(index, 1)}>
							<ArrowDown className="h-3.5 w-3.5" />
						</IconButton>
					</div>
					<IconButton label="Remove" onClick={() => remove(index)}>
						<Trash2 className="h-3.5 w-3.5" />
					</IconButton>
				</div>
			))}
			<button
				type="button"
				disabled={atCap}
				onClick={() => onChange([...value, blankItem()])}
				className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
			>
				<Plus className="h-3.5 w-3.5" />
				{atCap ? `Limit of ${field.max} reached` : 'Add'}
			</button>
		</div>
	)
}

function IconButton({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
	return (
		<button
			type="button"
			aria-label={label}
			title={label}
			disabled={disabled}
			onClick={onClick}
			className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
		>
			{children}
		</button>
	)
}

export default ConfigForm
